export const normalizeTickerSymbol = (symbol) =>
  String(symbol || "").toLowerCase().trim();

export const normalizeTickerStock = (stock) =>
  stock != null && String(stock).trim() !== ""
    ? String(stock).toLowerCase().trim()
    : "";

/** Internal dedup key — not used as a display symbol. */
export const getLivePriceTickerKey = ({ symbol, stock }) => {
  const sym = normalizeTickerSymbol(symbol);
  const st = normalizeTickerStock(stock);
  return st ? `${sym}|${st}` : sym;
};

/** Normalize stored entry; supports legacy composite symbol values (e.g. pbr/a). */
export const normalizeLivePriceEntry = (entry) => {
  if (!entry) {
    return { symbol: "", stock: "" };
  }

  let symbol = normalizeTickerSymbol(entry.symbol);
  let stock = normalizeTickerStock(entry.stock);

  if (!stock && symbol.includes("/")) {
    const [sym, st] = symbol.split("/");
    symbol = normalizeTickerSymbol(sym);
    stock = normalizeTickerStock(st);
  }

  return { symbol, stock };
};

/** Display label: `brk/b` when stock exists, otherwise `aapl`. */
export const formatTickerDisplay = (ticker) => {
  const { symbol, stock } = normalizeLivePriceEntry(ticker ?? {});
  if (!symbol) return "";
  return stock ? `${symbol}/${stock}` : symbol;
};

export const matchLivePriceToTicker = (livePriceEntry, ticker) => {
  if (!livePriceEntry || !ticker?.symbol) return false;

  const entry = normalizeLivePriceEntry(livePriceEntry);
  const lookup = {
    symbol: normalizeTickerSymbol(ticker.symbol),
    stock: normalizeTickerStock(ticker.stock),
  };

  return entry.symbol === lookup.symbol && entry.stock === lookup.stock;
};

export const findLivePriceForTicker = (livePrices, ticker) => {
  if (!Array.isArray(livePrices) || !ticker?.symbol) return undefined;
  return livePrices.find((entry) => matchLivePriceToTicker(entry, ticker));
};

export const parseTickerFieldsFromKeyMeta = (keyMeta) => {
  const symbol = normalizeTickerSymbol(keyMeta?.symbol || keyMeta?.Symbol);
  const stock = normalizeTickerStock(keyMeta?.stock);
  return { symbol, stock };
};

export const buildMarketDataTickerFields = (keyMeta) => {
  const { symbol, stock } = parseTickerFieldsFromKeyMeta(keyMeta);
  return {
    symbol,
    ...(stock && { stock }),
  };
};

export const mergeMarketDataEntries = (existing = [], incoming = []) => {
  const merged = existing.concat(incoming);

  return Array.from(
    merged
      .reduce((map, obj) => {
        const key = getLivePriceTickerKey(normalizeLivePriceEntry(obj));

        if (
          !map.has(key) ||
          new Date(obj.expiration) > new Date(map.get(key).expiration)
        ) {
          map.set(key, obj);
        }

        return map;
      }, new Map())
      .values()
  );
};
